import { create } from 'zustand'
import type {
  InputFormat,
  SP80022TestResult,
  SP80022SuiteResult,
  SP80090BResult,
  TunableBiasSuiteResult,
  GaussianSuiteResult,
} from '@/types'
import { type ParseResult, parseFile, parseText } from '@/lib/fileParser'

interface TestState {
  // Input
  inputFile: File | null
  inputData: ParseResult | null
  isLoading: boolean
  error: string | null

  // Active module
  activeModule: 'sp800-22' | 'sp800-90b' | 'tunable-bias' | 'gaussian' | null

  // Results
  sp80022Results: SP80022SuiteResult | null
  sp80090bResults: SP80090BResult | null
  tunableBiasResults: TunableBiasSuiteResult | null
  gaussianResults: GaussianSuiteResult | null

  // Progress
  isRunning: boolean
  currentTest: string | null
  progress: number
  progressResults: SP80022TestResult[]

  // Actions
  setInputFile: (file: File | null) => void
  setInputData: (data: ParseResult) => void
  loadFile: (file: File) => Promise<void>
  loadText: (text: string, format?: InputFormat) => void
  setActiveModule: (module: TestState['activeModule']) => void
  setProgress: (test: string, progress: number, result?: SP80022TestResult) => void
  setResults: (module: string, results: unknown) => void
  setRunning: (running: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
}

const initialState = {
  inputFile: null,
  inputData: null,
  isLoading: false,
  error: null,
  activeModule: null as TestState['activeModule'],
  sp80022Results: null,
  sp80090bResults: null,
  tunableBiasResults: null,
  gaussianResults: null,
  isRunning: false,
  currentTest: null,
  progress: 0,
  progressResults: [] as SP80022TestResult[],
}

export const useTestStore = create<TestState>((set) => ({
  ...initialState,

  setInputFile: (file: File | null) => {
    set({ inputFile: file })
  },

  setInputData: (data: ParseResult) => {
    set({ inputData: data, inputFile: null, error: null, isLoading: false })
  },

  loadFile: async (file: File) => {
    set({ isLoading: true, error: null, inputFile: file })
    try {
      const result = await parseFile(file)
      set({ inputData: result, isLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse file.'
      set({ error: message, isLoading: false, inputData: null })
    }
  },

  loadText: (text: string, format?: InputFormat) => {
    set({ isLoading: true, error: null, inputFile: null })
    try {
      const result = parseText(text, format)
      set({ inputData: result, isLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse text.'
      set({ error: message, isLoading: false, inputData: null })
    }
  },

  setActiveModule: (module: TestState['activeModule']) => {
    set({ activeModule: module })
  },

  setProgress: (test: string, progress: number, result?: SP80022TestResult) => {
    set((state) => ({
      currentTest: test,
      progress,
      progressResults: result
        ? [...state.progressResults, result]
        : state.progressResults,
    }))
  },

  setResults: (module: string, results: unknown) => {
    switch (module) {
      case 'sp800-22':
        set({ sp80022Results: results as SP80022SuiteResult })
        break
      case 'sp800-90b':
        set({ sp80090bResults: results as SP80090BResult })
        break
      case 'tunable-bias':
        set({ tunableBiasResults: results as TunableBiasSuiteResult })
        break
      case 'gaussian':
        set({ gaussianResults: results as GaussianSuiteResult })
        break
    }
  },

  setRunning: (running: boolean) => {
    set({
      isRunning: running,
      ...(running
        ? { currentTest: null, progress: 0, progressResults: [] }
        : {}),
    })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  reset: () => {
    set({ ...initialState })
  },
}))
